// src/components/map/utils/facilityIconSizeExpressions.ts
//
// Pure helper functions for building Mapbox GL style expressions
// for facility icon sizing in various contexts (highlight, overlay).
//
// The base expression `buildFacilityIconSizeExpression` lives in
// `networkMapStyles.ts`. This module adds the highlight and overlay
// variants used by MapboxWrapper and its decomposed hooks.

/**
 * Zoom stops shared across all facility icon-size expressions.
 * Each entry is [zoom, baseSize] — the baseSize is multiplied by
 * `sizeMultiplier` at runtime. Exported so the base facility builder in
 * `networkMapStyles.ts` scales the same curve.
 */
export const FACILITY_ZOOM_STOPS: ReadonlyArray<readonly [number, number]> = [
  [6, 0.2],
  [10, 1.0],
  [12, 1.5],
  [22, 2.0]
];

/**
 * Build a Mapbox `interpolate` expression over zoom, scaling each stop's base
 * size by `sizeMultiplier`. Shared by the facility, overlay, and rig/frac
 * icon-size builders so the curve shape and multiplier maths live in one place.
 *
 * @param stops          - Zoom stops as [zoom, baseSize] pairs.
 * @param sizeMultiplier - Multiplier applied to every stop's base size.
 * @returns A Mapbox style expression suitable for the `icon-size` layout property.
 */
export function buildInterpolatedIconSizeExpression(
  stops: ReadonlyArray<readonly [number, number]>,
  sizeMultiplier: number
): unknown[] {
  const flatStops: unknown[] = [];

  for (const [zoom, baseSize] of stops) {
    flatStops.push(zoom, baseSize * sizeMultiplier);
  }

  return ["interpolate", ["linear"], ["zoom"], ...flatStops];
}

/**
 * Zoom stops for highlighted / overlay facility icons.
 * These are larger than the base stops to make highlights visually distinct.
 */
const FACILITY_HIGHLIGHT_ZOOM_STOPS: ReadonlyArray<readonly [number, number]> = [
  [6, 0.6],
  [10, 1.8],
  [12, 2.2],
  [22, 3.0]
];

/**
 * Build a Mapbox `interpolate` expression for facility icon sizing
 * that conditionally enlarges facilities whose UWI appears in `facilityData`.
 *
 * Facilities present in `facilityData` get the highlight sizes;
 * those absent get the default sizes. Both scale with `sizeMultiplier`.
 *
 * @param sizeMultiplier - User-configured facility icon size multiplier (defaults to 1).
 * @param facilityData   - A record keyed by facility UWI. Presence in this object
 *                         determines whether a feature gets the enlarged size.
 * @returns A Mapbox style expression suitable for `icon-size` layout property.
 */
export function buildFacilityHighlightIconSizeExpression(
  sizeMultiplier: number,
  facilityData: Record<string, number>
): unknown[] {
  const stops: unknown[] = [];

  for (let i = 0; i < FACILITY_ZOOM_STOPS.length; i++) {
    const [zoom, baseSize] = FACILITY_ZOOM_STOPS[i];
    const [, highlightSize] = FACILITY_HIGHLIGHT_ZOOM_STOPS[i];

    stops.push(zoom, [
      "case",
      ["has", ["get", "Uwi"], ["literal", facilityData]],
      ["literal", highlightSize * sizeMultiplier],
      ["literal", baseSize * sizeMultiplier]
    ]);
  }

  return ["interpolate", ["linear"], ["zoom"], ...stops];
}

/**
 * Build a Mapbox `interpolate` expression for the highlighted-facility
 * overlay layer. This uses the larger highlight zoom stops uniformly
 * (no conditional — the overlay layer only contains highlighted features).
 *
 * @param sizeMultiplier - User-configured facility icon size multiplier (defaults to 1).
 * @returns A Mapbox style expression suitable for `icon-size` layout property.
 */
export function buildFacilityOverlayIconSizeExpression(
  sizeMultiplier: number
): unknown[] {
  return buildInterpolatedIconSizeExpression(
    FACILITY_HIGHLIGHT_ZOOM_STOPS,
    sizeMultiplier
  );
}
